import { useEffect, useState } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { CheckCircle, CheckSquare, Clock, Download, RefreshCw, XCircle, Zap } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { EdgeFunctionLogsService } from "@/services/EdgeFunctionLogsService";
import { EdgeFunctionLogDetailsSidebar } from "./EdgeFunctionLogDetailsSidebar";
import {
  useEdgeFunctionLog,
  useEdgeFunctionLogs,
  useEdgeFunctionLogStats,
  useResolveEdgeFunctionLog,
} from "./hooks/useEdgeFunctionLogs";
import { RefreshCountdown } from "../ConversationLogs/RefreshCountdown";

import type { EdgeFunctionLog, EdgeFunctionLogFilters } from "@/types/edge-function-logs";

const FUNCTION_NAMES = [
  "doc-upload",
  "handle-agent",
  "handle-conversation",
  "handle-profile",
  "ai-chat",
  "brain-chat",
  "brain-chat-worker",
  "conversation-digest",
  "elevenlabs",
  "email-handler",
  "genie-integrity-audit",
  "image-handler",
  "inbox-view",
  "knowledge-watch",
  "send-postmark",
  "twilio-handler",
  "stripe-handler",
  "webhooks",
  "api",
];
const PAGE_SIZE_OPTIONS = [30, 50, 100, 200];
const DEFAULT_PAGE_SIZE = 30;

type DateRangeOption = "today" | "last_week" | "last_month" | "all_time";

function getDateRange(option: DateRangeOption): { date_from?: string; date_to?: string } {
  if (option === "all_time") return {};

  const now = new Date();
  const from = new Date(now);

  if (option === "today") {
    from.setHours(0, 0, 0, 0);
  } else if (option === "last_week") {
    from.setDate(from.getDate() - 7);
  } else {
    from.setMonth(from.getMonth() - 1);
  }

  return { date_from: from.toISOString() };
}

const DEFAULT_DATE_RANGE: DateRangeOption = "today";

const EdgeFunctionLogsPage = () => {
  const [dateRange, setDateRange] = useState<DateRangeOption>(DEFAULT_DATE_RANGE);
  const [filters, setFilters] = useState<EdgeFunctionLogFilters>({
    status: "failed",
    ...getDateRange(DEFAULT_DATE_RANGE),
  });
  const [callerEmail, setCallerEmail] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedLog, setSelectedLog] = useState<EdgeFunctionLog | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Slack error alerts link here with ?logId=<id> to open that log's details directly.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedLogId = searchParams.get("logId");
  const { data: linkedLog, isError: isLinkedLogMissing } = useEdgeFunctionLog(linkedLogId);

  const clearLinkedLog = () => {
    if (!searchParams.has("logId")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("logId");
    setSearchParams(next, { replace: true });
  };

  const applyCallerEmail = (value: string) => {
    const email = value.trim() || undefined;
    if (email === filters.caller_email) return;
    setFilters({ ...filters, caller_email: email });
    setPage(1);
  };

  useEffect(() => {
    if (!linkedLog) return;
    setSelectedLog(linkedLog);
    setIsDetailOpen(true);
  }, [linkedLog]);

  useEffect(() => {
    if (!isLinkedLogMissing) return;
    toast.error("This edge function log no longer exists");
    clearLinkedLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLinkedLogMissing]);

  const {
    data: logsData,
    isLoading,
    dataUpdatedAt,
    refetch: refetchLogs,
    isFetching: isFetchingLogs,
  } = useEdgeFunctionLogs(filters, page, pageSize, !isDetailOpen);
  const { data: stats, refetch: refetchStats } = useEdgeFunctionLogStats(filters, !isDetailOpen);

  const handleManualRefresh = () => {
    refetchLogs();
    refetchStats();
  };
  const { mutate: resolveLog, isPending: isResolving } = useResolveEdgeFunctionLog();

  const handleExport = async () => {
    try {
      const csvContent = await EdgeFunctionLogsService.exportToCSV(filters);
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `edge-function-logs-${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      toast.success("Edge function logs exported");
    } catch (error: any) {
      toast.error(`Export failed: ${error.message}`);
    }
  };

  const handleViewDetails = (log: EdgeFunctionLog) => {
    setSelectedLog(log);
    setIsDetailOpen(true);
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { icon: any; className: string; label: string }> = {
      invoked: {
        icon: RefreshCw,
        className: "bg-genie-primary-light text-genie-primary border-genie-primary/20",
        label: "Invoked",
      },
      completed: {
        icon: CheckCircle,
        className: "bg-genie-primary-light text-genie-primary border-genie-primary/20",
        label: "Completed",
      },
      failed: { icon: XCircle, className: "bg-genie-error-light text-genie-error border-genie-error/20", label: "Failed" },
    };

    const config = statusMap[status] || statusMap.invoked;
    const Icon = config.icon;

    return (
      <Badge className={cn(config.className, "inline-flex items-center gap-1 border w-fit")}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getOccurrenceCount = (log: EdgeFunctionLog) =>
    log.occurrence_count >= 1 && (
      <span
        className="text-xs font-semibold text-genie-text-secondary"
        title={
          log.occurrence_count > 1
            ? `Happened ${log.occurrence_count} times, each within 10 minutes of the previous one`
            : undefined
        }
      >
        ×{log.occurrence_count}
      </span>
    );

  return (
    <div className="min-h-screen bg-genie-background p-4 md:p-8">
      <div className="max-w-[1920px] mx-auto">
        {/* Header */}
        <div className="flex flex-col items-start gap-4 mb-8 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <Zap className="h-8 w-8 shrink-0 text-genie-secondary" />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-genie-text-primary">Edge Function Logs</h1>
              <p className="text-genie-text-secondary">Inspect every edge function invocation and debug failures</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 md:mr-36">
            <RefreshCountdown
              isVisible={!isDetailOpen}
              dataUpdatedAt={dataUpdatedAt}
              onRefresh={handleManualRefresh}
              isRefreshing={isFetchingLogs}
            />
            <Button onClick={handleExport} variant="outline" className="gap-2">
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg p-6 border border-genie-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-genie-text-secondary">Failures</p>
                  <p className="text-2xl font-bold text-genie-error">{stats.failures}</p>
                </div>
                <XCircle className="h-8 w-8 text-genie-error" />
              </div>
            </div>
            <div className="bg-white rounded-lg p-6 border border-genie-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-genie-text-secondary">Occurrences</p>
                  <p className="text-2xl font-bold text-genie-text-primary">{stats.occurrences}</p>
                </div>
                <Zap className="h-8 w-8 text-genie-primary" />
              </div>
            </div>
            <div className="bg-white rounded-lg p-6 border border-genie-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-genie-text-secondary">Unresolved</p>
                  <p className="text-2xl font-bold text-genie-error">{stats.unresolved}</p>
                </div>
                <Clock className="h-8 w-8 text-genie-error" />
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg p-4 mb-6 border border-genie-border">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <Select
              value={dateRange}
              onValueChange={(value) => {
                const option = value as DateRangeOption;
                setDateRange(option);
                setFilters({ ...filters, ...getDateRange(option), date_to: undefined });
                setPage(1);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Date Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="last_week">Last Week</SelectItem>
                <SelectItem value="last_month">Last Month</SelectItem>
                <SelectItem value="all_time">All Time</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.guest_only ? "guest" : "all"}
              onValueChange={(value) => {
                setFilters({ ...filters, guest_only: value === "guest" || undefined });
                setPage(1);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Caller" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Callers</SelectItem>
                <SelectItem value="guest">No User (Guest)</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="search"
              placeholder="Caller email, press Enter"
              value={callerEmail}
              disabled={filters.guest_only}
              onChange={(event) => {
                setCallerEmail(event.target.value);
                if (!event.target.value) applyCallerEmail("");
              }}
              onKeyDown={(event) => event.key === "Enter" && applyCallerEmail(callerEmail)}
              onBlur={() => applyCallerEmail(callerEmail)}
            />
            <Select
              value={filters.function_name || "all"}
              onValueChange={(value) => setFilters({ ...filters, function_name: value === "all" ? undefined : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Function" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Functions</SelectItem>
                {FUNCTION_NAMES.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant={filters.unresolved_only ? "default" : "outline"}
              onClick={() => setFilters({ ...filters, unresolved_only: !filters.unresolved_only })}
              className="w-full"
            >
              Unresolved Only
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setDateRange(DEFAULT_DATE_RANGE);
                setCallerEmail("");
                setFilters({ status: "failed", ...getDateRange(DEFAULT_DATE_RANGE) });
                setPage(1);
              }}
              className="w-full"
            >
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Logs Table */}
        <div className="bg-white rounded-lg border border-genie-border">
          {/* Desktop Table */}
          <div className="hidden md:block overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Function</TableHead>
                  <TableHead>Error</TableHead>
                  <TableHead>Occurrences</TableHead>
                  <TableHead>Caller</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Resolved</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-genie-text-secondary">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-genie-primary" />
                      Loading edge function logs...
                    </TableCell>
                  </TableRow>
                ) : logsData?.data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-genie-text-muted">
                      No edge function logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  logsData?.data.map((log) => (
                    <TableRow
                      key={log.id}
                      className="cursor-pointer hover:bg-genie-background-muted"
                      onClick={() => handleViewDetails(log)}
                    >
                      <TableCell>
                        <p className="font-medium text-genie-text-primary">{log.function_name}</p>
                        {log.action && <p className="text-xs text-genie-text-muted mt-1">action: <code className="bg-gray-100 px-1 py-0.5 rounded font-mono text-genie-text-primary">{log.action}</code></p>}
                        {log.resource && <p className="text-xs text-genie-text-muted mt-1">resource: <code className="bg-gray-100 px-1 py-0.5 rounded font-mono text-genie-text-primary">{log.resource}</code></p>}
                      </TableCell>
                      <TableCell className="max-w-sm">
                        {log.error_message ? (
                          <p
                            className="text-xs text-genie-text-secondary line-clamp-2 break-words"
                            title={log.error_message}
                          >
                            {log.error_message}
                          </p>
                        ) : (
                          <span className="text-genie-text-muted">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(log.status)}
                          {getOccurrenceCount(log)}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-genie-text-secondary truncate max-w-xs">
                        <div className="flex flex-col items-start">
                          <CopyableEmail email={log.caller_email} />
                          {log.is_impersonated && <Badge variant="secondary" className="-mt-2.5">Impersonated</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-genie-text-secondary">
                        {new Date(log.created_at).toLocaleString()}
                        {log.occurrence_count > 1 && (
                          <p className="text-xs text-genie-text-muted">
                            Last: {new Date(log.last_occurred_at).toLocaleString()}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-genie-text-secondary">
                        <div className="flex items-center gap-1">
                          {log.status !== "failed" ? "—" : log.resolved_at ? "Yes" : "No"}
                          {log.status === "failed" && !log.resolved_at && (
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Mark as resolved"
                              disabled={isResolving}
                              onClick={(event) => {
                                event.stopPropagation();
                                resolveLog(log.id, { onSuccess: () => toast.success("Marked as resolved") });
                              }}
                            >
                              <CheckSquare className="h-4 w-4 text-genie-primary" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden">
            {isLoading ? (
              <div className="text-center py-8 text-genie-text-secondary">
                <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-genie-primary" />
                <p>Loading edge function logs...</p>
              </div>
            ) : logsData?.data.length === 0 ? (
              <div className="text-center py-8 text-genie-text-muted">No edge function logs found</div>
            ) : (
              <div className="space-y-3 p-4">
                {logsData?.data.map((log) => (
                  <div
                    key={log.id}
                    className="border border-genie-border rounded-lg p-4 space-y-3 cursor-pointer"
                    onClick={() => handleViewDetails(log)}
                  >
                    <div className="flex flex-col items-start gap-1">
                      <p className="font-medium text-genie-text-primary text-sm">{log.function_name}</p>
                      {log.action && <p className="text-xs text-genie-text-muted">action: <code className="bg-gray-100 px-1 py-0.5 rounded font-mono text-genie-text-primary">{log.action}</code></p>}
                      {log.resource && <p className="text-xs text-genie-text-muted">resource: <code className="bg-gray-100 px-1 py-0.5 rounded font-mono text-genie-text-primary">{log.resource}</code></p>}
                      <CopyableEmail email={log.caller_email} className="text-xs text-genie-text-secondary" />
                      {log.is_impersonated && <Badge variant="secondary" className="-mt-2.5">Impersonated</Badge>}
                    </div>

                    <div className="flex items-center gap-2">
                      {getStatusBadge(log.status)}
                      {getOccurrenceCount(log)}
                    </div>

                    <div className="text-xs text-genie-text-secondary space-y-1">
                      {log.error_message && (
                        <p className="line-clamp-2 break-words" title={log.error_message}>
                          <span className="text-genie-text-muted">Error:</span> {log.error_message}
                        </p>
                      )}
                      <p><span className="text-genie-text-muted">Created:</span> {new Date(log.created_at).toLocaleString()}</p>
                      {log.occurrence_count > 1 && (
                        <p><span className="text-genie-text-muted">Last:</span> {new Date(log.last_occurred_at).toLocaleString()}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {logsData && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-genie-border text-sm">
              <div className="flex items-center gap-4 order-2 sm:order-1">
                <span className="text-genie-text-secondary">
                  Showing {logsData.count === 0 ? 0 : (page - 1) * pageSize + 1} to {Math.min(page * pageSize, logsData.count)} of{" "}
                  {logsData.count} logs
                </span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => {
                    setPageSize(Number(value));
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[110px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size} / page
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 order-1 sm:order-2 w-full sm:w-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="flex-1 sm:flex-none"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page * pageSize >= logsData.count}
                  className="flex-1 sm:flex-none"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Detail Drawer */}
        <EdgeFunctionLogDetailsSidebar
          isOpen={isDetailOpen}
          onClose={() => {
            setIsDetailOpen(false);
            clearLinkedLog();
          }}
          selectedLog={selectedLog}
          getStatusBadge={getStatusBadge}
          onResolve={(id) => resolveLog(id, { onSuccess: () => toast.success("Marked as resolved") })}
          isResolving={isResolving}
        />
      </div>
    </div>
  );
};

export default EdgeFunctionLogsPage;
